import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listDocAiSummaries } from './docAiSummaryApi';

// Loads the summary status for every document on the transaction once, so
// the ⋮ menu can show Generate / Generating… / View without a request per row.
//
// The list call doubles as the access check: it only succeeds for a primary
// party (FR-14), so `enabled` means "feature on AND this viewer may use it".
//   404 (feature off) / 403 (not a primary party) → hidden.
//   Any other failure (network, 5xx, timeout)    → `loadError`, so the menu can
//   show an error state with Retry instead of silently hiding (FR-4).
// Never throws during render, so it can't affect TransactionDocuments.
//
// options.viewerIsPrimary: the viewer's participant row is marked primary. Only
// used to decide whether to show the error state; the server decides access.
const useDocAiSummaries = (transactionId, { viewerIsPrimary = false } = {}) => {
  const [enabled, setEnabled] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [target, setTarget] = useState(null); // { doc, sourceKey, summaryId, mode }
  const mounted = useRef(true);

  const refresh = useCallback(async () => {
    if (!transactionId) return;
    try {
      const list = await listDocAiSummaries(transactionId);
      if (!mounted.current) return;
      setStatuses(list);
      setEnabled(true);
      setLoadError(false);
    } catch (err) {
      if (!mounted.current) return;
      const status = err?.response?.status;
      setEnabled(false);
      setStatuses([]);
      setLoadError(status !== 404 && status !== 403 && status !== 401);
    }
  }, [transactionId]);

  useEffect(() => {
    mounted.current = true;
    refresh();
    return () => { mounted.current = false; };
  }, [refresh]);

  const byKey = useMemo(() => {
    const map = new Map();
    statuses.forEach((s) => map.set(`${s.sourceKind}:${s.sourceRef}`, s));
    return map;
  }, [statuses]);

  const statusFor = useCallback((key) => (key ? byKey.get(key) || null : null), [byKey]);

  // extra.clientStale: Forms rows — the web detected a newer version of the row (FR-11).
  const open = useCallback((doc, sourceKey, mode, extra = {}) => {
    const status = byKey.get(sourceKey.key);
    setTarget({ doc, sourceKey, summaryId: status?.summaryId || null, mode, clientStale: !!extra.clientStale });
  }, [byKey]);

  const close = useCallback(() => {
    setTarget(null);
    refresh();
  }, [refresh]);

  return { transactionId, enabled, loadError, viewerIsPrimary, statusFor, refresh, target, open, close };
};

export default useDocAiSummaries;
